import React, { useState } from 'react';
import { Alert, Button, Card, Form } from 'react-bootstrap';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiGift } from 'react-icons/fi';
import { createCommercialGesture, getPolicyCommercialGesture } from '../../services/api';
import {
    getCgErrorToast,
    getCgFieldErrors,
    parseCgAmount,
    parseCgError,
    unwrapCgData,
} from './commercialGestures/commercialGestureUtils';
import {
    CgAmountField,
    CgPolicyPreview,
    CgToastStack,
    useCgToasts,
    useCommercialGesturePermissions,
} from './commercialGestures/CommercialGestureShared';
import PolicyAutocomplete from './commercialGestures/PolicyAutocomplete';
import './commercialGestures/commercialGestures.css';

const AdminCommercialGestureCreate = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { canWrite } = useCommercialGesturePermissions();
    const { toasts, push, dismiss } = useCgToasts();
    const [policy, setPolicy] = useState(location.state?.policy || null);
    const [amount, setAmount] = useState('');
    const [fieldErrors, setFieldErrors] = useState({});
    const [conflict, setConflict] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async (event) => {
        event.preventDefault();
        setFieldErrors({});
        setConflict(null);

        const nextErrors = {};
        if (!policy?.policy_id) nextErrors.policy_id = 'Sélectionnez une police.';
        const parsedAmount = parseCgAmount(amount);
        if (parsedAmount.error) nextErrors.amount = parsedAmount.error;
        if (Object.keys(nextErrors).length > 0) {
            setFieldErrors(nextErrors);
            return;
        }

        setSubmitting(true);
        try {
            const response = await createCommercialGesture({
                policy_id: policy.policy_id,
                amount: parsedAmount.value,
            });
            const created = unwrapCgData(response);
            navigate(`/admin/trips/commercial-gestures/${created.id}`, {
                state: { toast: { message: 'Geste commercial créé.', variant: 'success' } },
            });
        } catch (err) {
            const parsed = parseCgError(err);
            if (parsed.status === 409) {
                let existingId = parsed.details?.id || parsed.details?.commercial_gesture_id;
                const policyId = parsed.details?.policy_id || policy.policy_id;
                if (!existingId && policyId) {
                    try {
                        const existing = unwrapCgData(await getPolicyCommercialGesture(policyId));
                        existingId = existing?.id;
                    } catch {
                        existingId = null;
                    }
                }
                setConflict({
                    policyId,
                    existingId,
                });
                push('Cette police a déjà un geste commercial', 'danger');
            } else if (parsed.status === 422) {
                setFieldErrors(getCgFieldErrors(parsed));
                push(getCgErrorToast(parsed), 'danger');
            } else {
                push(getCgErrorToast(parsed), 'danger');
            }
        } finally {
            setSubmitting(false);
        }
    };

    if (!canWrite) {
        return (
            <div className="dashboard-page">
                <Alert variant="danger">Action non autorisée</Alert>
            </div>
        );
    }

    return (
        <div className="dashboard-page">
            <CgToastStack toasts={toasts} onDismiss={dismiss} />
            <Card>
                <Card.Body>
                    <div className="page-header">
                        <div>
                            <h2 className="page-title">
                                <FiGift className="me-2" aria-hidden="true" />
                                Nouveau geste commercial
                            </h2>
                        </div>
                        <Button variant="outline-secondary" onClick={() => navigate('/admin/trips/commercial-gestures')}>
                            <FiArrowLeft className="me-2" />
                            Retour
                        </Button>
                    </div>

                    <Form className="mt-4" onSubmit={handleSubmit} noValidate style={{ maxWidth: 640 }}>
                        <PolicyAutocomplete
                            value={policy}
                            onSelect={setPolicy}
                            error={fieldErrors.policy_id}
                            disabled={submitting}
                        />
                        <CgPolicyPreview policy={policy} />
                        <CgAmountField
                            id="cg-create-amount"
                            value={amount}
                            onChange={setAmount}
                            error={fieldErrors.amount}
                            disabled={submitting}
                        />

                        {conflict && (
                            <Alert variant="warning">
                                Cette police a déjà un geste commercial.
                                {conflict.existingId ? (
                                    <>
                                        {' '}
                                        <Link
                                            className="cg-conflict-link"
                                            to={`/admin/trips/commercial-gestures/${conflict.existingId}`}
                                        >
                                            Voir le geste existant
                                        </Link>
                                    </>
                                ) : conflict.policyId ? (
                                    <>
                                        {' '}
                                        <Link
                                            className="cg-conflict-link"
                                            to={`/admin/trips/policies/${conflict.policyId}`}
                                        >
                                            Voir la police
                                        </Link>
                                    </>
                                ) : null}
                            </Alert>
                        )}

                        <Button type="submit" variant="primary" disabled={submitting}>
                            {submitting ? 'Enregistrement…' : 'Créer'}
                        </Button>
                    </Form>
                </Card.Body>
            </Card>
        </div>
    );
};

export default AdminCommercialGestureCreate;
